import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { ACTIVATE_USER, ADD_USER, DEACTIVATE_USER, DELETE_USER, GET_ALL_USERS } from './superAdmin';

const UPDATE_USER_MUTATION = gql`
   mutation UpdateUserProfile($userId: ID!, $input: UpdateUserProfileInput!) {
      updateUserProfile(userId: $userId, input: $input) {
         id
         username
         firstName
         lastName
         email
         phone
         company
         address
      }
   }
`;

const useUsers = () => {
   const { data, loading, error, refetch } = useQuery(GET_ALL_USERS, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true
   });

   const [addUserMutation] = useMutation(ADD_USER);
   const [updateUserMutation] = useMutation(UPDATE_USER_MUTATION);
   const [deactivateUserMutation] = useMutation(DEACTIVATE_USER);
   const [activateUserMutation] = useMutation(ACTIVATE_USER);
   const [deleteUserMutation] = useMutation(DELETE_USER);

   const addUser = async (newUser) => {
      try {
         console.log('Attempting to add user with data:', {
            ...newUser,
            password: '[REDACTED]' // Don't log the actual password
         });

         const { data, errors } = await addUserMutation({
            variables: {
               input: {
                  firstName: newUser.firstName,
                  lastName: newUser.lastName,
                  username: newUser.username,
                  password: newUser.password,
                  email: newUser.email,
                  role: newUser.role,
                  userType: newUser.userType
               }
            },
            refetchQueries: [{ query: GET_ALL_USERS }],
            awaitRefetchQueries: true
         });

         console.log('Mutation response:', { data, errors });

         if (errors) {
            throw new Error(errors[0]?.message || 'Unknown error occurred');
         }

         if (!data?.registerUser) {
            throw new Error('Failed to add user - No data returned');
         }

         toast.success('User added successfully');
         return data.registerUser.user;
      } catch (err) {
         console.error('Error adding user:', {
            message: err.message,
            graphQLErrors: err.graphQLErrors,
            networkError: err.networkError,
            // Log the full error but sanitize any sensitive data
            fullError: JSON.stringify(err, (key, value) =>
               key === 'password' ? '[REDACTED]' : value
            )
         });

         toast.error('Failed to add user', {
            description: err.message || 'An unknown error occurred'
         });
         throw err;
      }
   };

   const updateUser = async (userId, updatedUser) => {
      try {
         console.log('Attempting to update user:', { userId, updatedUserData: updatedUser });

         // Create a new object without userId (in case it's included)
         const { userId: _, ...inputData } = updatedUser;

         console.log('Sending to GraphQL:', { userId, input: inputData });

         const { data } = await updateUserMutation({
            variables: {
               userId,
               input: inputData // Send clean input without userId
            },
            refetchQueries: [{ query: GET_ALL_USERS }]
         });

         if (!data?.updateUserProfile) {
            throw new Error('Failed to update user profile');
         }

         toast.success('User updated successfully');
         return data.updateUserProfile;
      } catch (err) {
         console.error('Error updating user:', {
            message: err.message,
            graphQLErrors: err.graphQLErrors,
            networkError: err.networkError
         });

         toast.error('Failed to update user', {
            description: err.message
         });
         throw err;
      }
   };

   const deactivateUser = async (userId) => {
      try {
         await deactivateUserMutation({
            variables: { id: userId },
            refetchQueries: [{ query: GET_ALL_USERS }]
         });

         toast.success('User deactivated successfully');
         return true;
      } catch (err) {
         toast.error('Failed to deactivate user', {
            description: err.message
         });
         throw err;
      }
   };

   const activateUser = async (userId) => {
      try {
         await activateUserMutation({
            variables: { id: userId },
            refetchQueries: [{ query: GET_ALL_USERS }]
         });

         toast.success('User activated successfully');
         return true;
      } catch (err) {
         toast.error('Failed to activate user', {
            description: err.message
         });
         throw err;
      }
   };

   const deleteUser = async (userId) => {
      try {
         await deleteUserMutation({
            variables: { id: userId },
            refetchQueries: [{ query: GET_ALL_USERS }]
         });

         toast.success('User deleted successfully');
         return true;
      } catch (err) {
         toast.error('Failed to delete user', {
            description: err.message
         });
         throw err;
      }
   };

   const handleRefetch = useCallback(async () => {
      try {
         await refetch();
      } catch (error) {
         console.error('Error refetching users:', error);
      }
   }, [refetch]);

   return {
      users: data?.users || [],
      loading,
      error: error?.message,
      updateUser,
      addUser,
      deactivateUser,
      activateUser,
      deleteUser,
      refetch: handleRefetch
   };
};

export default useUsers;
